import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5001';

    // Create socket connection
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true,
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      // Join user-specific room
      newSocket.emit('join-room', `user-${user._id}`);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Listen for content approval
    newSocket.on('content-approved', (data) => {
      console.log('Content approved:', data);
      toast.success(`Your content "${data.content?.filename}" has been approved!`, {
        autoClose: 5000,
      });
    });

    // Listen for content rejection
    newSocket.on('content-rejected', (data) => {
      console.log('Content rejected:', data);
      toast.error(
        `Your content "${data.content?.filename}" was rejected. Reason: ${data.reason || 'Not specified'}`,
        {
          autoClose: 7000,
        }
      );
    });

    // Listen for playlist updates
    newSocket.on('playlist-updated', (data) => {
      console.log('Playlist updated:', data);
      toast.info(`Playlist "${data.playlist?.name}" has been updated`);
    });

    // Listen for system announcements
    newSocket.on('system-announcement', (data) => {
      console.log('System announcement:', data);
      toast.info(data.message, {
        autoClose: 10000,
      });
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.close();
    };
  }, [isAuthenticated, user]);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};